import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

const CustomerLogin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const restaurantId = searchParams.get('restaurantId');
    const tableId = searchParams.get('tableId');

    const [restaurant, setRestaurant] = useState(null);
    const [isLogin, setIsLogin] = useState(true);
    const [formData, setFormData] = useState({
        phoneNumber: '',
        password: '',
        name: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [messageType, setMessageType] = useState('error');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const currentRestaurantId = restaurantId || localStorage.getItem('restaurantId');
        if (currentRestaurantId) {
            fetchRestaurantDetails();
        }
    }, [restaurantId]);

    const fetchRestaurantDetails = async () => {
        try {
            const currentRestaurantId = restaurantId || localStorage.getItem('restaurantId');
            const response = await axios.get(API_ENDPOINTS.RESTAURANT_BY_ID(currentRestaurantId));
            setRestaurant(response.data);
        } catch (err) {
            setError('Restaurant not found');
            setMessageType('error');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (isLogin) {
                const response = await axios.post(API_ENDPOINTS.AUTH_LOGIN, {
                    phoneNumber: formData.phoneNumber,
                    password: formData.password,
                    userType: 'customer'
                });

                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('userData', JSON.stringify({ ...response.data.user, role: 'customer' }));
                localStorage.setItem('restaurantId', restaurantId || localStorage.getItem('restaurantId'));
                localStorage.setItem('tableId', tableId || localStorage.getItem('tableId'));

                const guestOrder = localStorage.getItem('guestOrder');
                const guestCustomerInfo = localStorage.getItem('guestCustomerInfo');

                if (guestOrder) {
                    localStorage.setItem('restoreOrder', guestOrder);
                    localStorage.setItem('restoreCustomerInfo', guestCustomerInfo);
                    localStorage.removeItem('guestOrder');
                    localStorage.removeItem('guestCustomerInfo');
                }

                window.location.href = '/customer';
            } else {
                if (formData.password !== formData.confirmPassword) {
                    setError('Passwords do not match');
                    setMessageType('error');
                    return;
                }

                await axios.post(API_ENDPOINTS.AUTH_REGISTER_CUSTOMER, {
                    name: formData.name,
                    phoneNumber: formData.phoneNumber,
                    password: formData.password
                });

                setIsLogin(true);
                setError('Registration successful! Please login.');
                setMessageType('success');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred');
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "w-full px-3 py-2.5 sm:px-4 sm:py-3 bg-stone-50 border border-stone-200 rounded-lg sm:rounded-xl text-stone-900 text-sm placeholder-stone-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors";
    const labelClass = "block text-xs sm:text-sm font-medium text-stone-700 mb-1.5 sm:mb-2";

    if (!restaurantId && !tableId && !localStorage.getItem('restaurantId') && !localStorage.getItem('tableId')) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
                <div className="fixed inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
                    <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/25 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                </div>
                <div className="relative z-10 text-center p-6 sm:p-8 bg-white rounded-2xl border border-stone-200 shadow-lg max-w-sm w-full">
                    <div className="text-4xl sm:text-5xl mb-3 sm:mb-4">❌</div>
                    <h2 className="text-xl sm:text-2xl font-bold text-stone-800 mb-2">Invalid Access</h2>
                    <p className="text-stone-600 text-sm sm:text-base mb-4">Please scan a valid restaurant table QR code</p>
                    <Link
                        to="/"
                        className="inline-block bg-amber-500 hover:bg-amber-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-colors"
                    >
                        Go to Home
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center p-3 sm:p-4 py-6 sm:py-8 overflow-hidden">
            {/* Background */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
                <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/25 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[60vmax] h-[60vmax] bg-amber-100/30 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(251,191,36,0.08),transparent)]" />
            </div>

            <div className="relative z-10 max-w-md w-full">
                {/* Restaurant Branding */}
                {restaurant && (
                    <div className="text-center mb-5 sm:mb-8">
                        <div className="relative inline-block">
                            <img
                                src={restaurant.logo || "/logo-bright.svg"}
                                alt={restaurant.name}
                                className="h-14 w-14 sm:h-16 sm:w-16 md:h-20 md:w-20 mx-auto rounded-xl sm:rounded-2xl shadow-md object-cover"
                            />
                            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-white">
                                <span className="text-white text-[10px] sm:text-xs">✓</span>
                            </div>
                        </div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-800 mt-3 sm:mt-4">{restaurant.name}</h1>
                        <p className="text-stone-600 text-sm sm:text-base mt-1 sm:mt-2">Welcome to our digital menu experience</p>
                    </div>
                )}

                {/* Login / Create Account Card */}
                <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-stone-200/80 p-4 sm:p-6 md:p-8">
                    <div className="text-center mb-4 sm:mb-6">
                        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-stone-900">
                            {isLogin ? 'Customer Login' : 'Create Account'}
                        </h2>
                        <p className="text-stone-600 text-xs sm:text-sm mt-1 sm:mt-2">
                            {isLogin ? 'Enter your phone number to continue' : 'Register to access our menu'}
                        </p>
                    </div>

                    {error && (
                        <div className={`px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl mb-3 sm:mb-4 text-sm ${messageType === 'success'
                                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                                : 'bg-red-50 border border-red-200 text-red-700'
                            }`}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                        {!isLogin && (
                            <div>
                                <label className={labelClass}>Full Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className={inputClass}
                                    placeholder="Enter your full name"
                                />
                            </div>
                        )}

                        <div>
                            <label className={labelClass}>Phone Number</label>
                            <input
                                type="tel"
                                required
                                placeholder="Enter 10-digit number"
                                maxLength="10"
                                value={formData.phoneNumber}
                                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                                className={inputClass}
                            />
                        </div>

                        <div>
                            <label className={labelClass}>Password</label>
                            <input
                                type="password"
                                required
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                className={inputClass}
                                placeholder={isLogin ? 'Enter your password' : 'Choose a password'}
                            />
                        </div>

                        {!isLogin && (
                            <div>
                                <label className={labelClass}>Confirm Password</label>
                                <input
                                    type="password"
                                    required
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                    className={inputClass}
                                    placeholder="Confirm your password"
                                />
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 sm:py-3 px-4 rounded-lg sm:rounded-xl transition-all duration-300 shadow-lg shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                        >
                            {loading ? 'Please wait...' : (isLogin ? 'Login' : 'Create Account')}
                        </button>
                    </form>

                    <div className="mt-4 sm:mt-6 text-center">
                        <button
                            type="button"
                            onClick={() => { setIsLogin(!isLogin); setError(''); }}
                            className="text-amber-600 hover:text-amber-700 font-medium text-sm sm:text-base"
                        >
                            {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
                        </button>
                    </div>
                </div>

                <div className="mt-4 sm:mt-6 text-center">
                    <Link to="/" className="text-stone-500 hover:text-amber-600 text-xs sm:text-sm font-medium transition-colors">
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CustomerLogin;
